import React, { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useBlocker } from 'react-router-dom';

type UnsavedChangesModalProps = {
  when: boolean;
  onConfirmLeave?: () => void;
};

/**
 * Blocks in-app navigation and browser unload while `when` is true. When the
 * user attempts to navigate, shows a modal asking whether to discard review
 * progress or stay on the page.
 */
const UnsavedChangesModal = ({ when, onConfirmLeave }: UnsavedChangesModalProps) => {
  const blocker = useBlocker(when);

  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  const isBlocked = blocker.state === 'blocked';

  const handleLeave = () => {
    if (onConfirmLeave) onConfirmLeave();
    if (blocker.state === 'blocked') blocker.proceed();
  };

  const handleStay = () => {
    if (blocker.state === 'blocked') blocker.reset();
  };

  return (
    <Dialog open={isBlocked} onClose={handleStay} fullWidth maxWidth="xs">
      <DialogTitle>You have unsaved changes</DialogTitle>
      <DialogContent>
        <Typography variant="body" color="text.secondary">
          If you leave, your selections won&apos;t be applied to the profile.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleLeave}>
          Leave Anyway
        </Button>
        <Button variant="outlined" color="primary" onClick={handleStay}>
          Keep Editing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnsavedChangesModal;
